import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  List,
  ListItem,
  Theme,
  Typography,
  Drawer,
  IconButton,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Close from '@mui/icons-material/Close';
import Menu from '@mui/icons-material/Menu';
import { getHeaderInfo } from '../../../../lib/helper/RoomStructureHelper';
import { RoomSectionHeaderProps } from './types';
import { RoomStructureHeader } from '../../../../definitions/schemas';
import RoomAppBar, { RoomAppBarProps } from '../../../Rooms/RoomAppBar';

const useStyles = makeStyles((theme: Theme) => ({
  list: {
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  listItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 0),
    textTransform: 'none',
  },
  paper: {
    width: '100%',
  },
}));

interface Props extends RoomSectionHeaderProps {
  AppBarProps?: RoomAppBarProps;
  initialOpen?: boolean;
  renderRoomConversation: (
    isMenuItem: boolean,
    displayMobileCounter?: boolean,
    onClose?: () => void
  ) => React.ReactNode;
  renderShareRoom: (
    isMenuItem: boolean,
    isFloatingActionButton?: boolean
  ) => React.ReactNode;
}

const RoomHeadersMenuList: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = useStyles();

  const {
    AppBarProps,
    initialOpen,
    onClick,
    renderRoomConversation,
    renderShareRoom,
    structureHeaders,
  } = props;

  const [openMenu, setOpenMenu] = useState(false);

  /**
   * Whether the drawer was forcefully opened because the room was deep linked
   * to a specific conversation message. This determines whether the user
   * exits back to the room or keeps the drawer open (e.g. when the user
   * opens the drawer on purpose and then opens the room's conversation)
   */
  const wasForcedOpen = useRef(initialOpen);

  useEffect(() => {
    if (!initialOpen) {
      return;
    }

    setOpenMenu(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMenu = () => {
    wasForcedOpen.current = false;
    setOpenMenu(!openMenu);
  };

  const handleOnClick = (header: RoomStructureHeader) => {
    toggleMenu();
    onClick(header);
  };

  const onConversationClose = () => {
    if (wasForcedOpen.current) {
      setOpenMenu(false);
    }
  };

  const renderHeaderItems = () =>
    structureHeaders.map((header) => {
      const { id } = header;
      const [name] = getHeaderInfo(header);

      return (
        <ListItem
          button
          classes={{ root: classes.listItem }}
          key={`header-${id}`}
          onClick={() => handleOnClick(header)}
        >
          <Typography color="textSecondary" variant="body2">
            {name}
          </Typography>
        </ListItem>
      );
    });

  return (
    <Box alignItems="center" display="flex" ml={6}>
      {renderRoomConversation(false, true, onConversationClose)}
      <IconButton onClick={toggleMenu} size="large">
        <Menu sx={{ color: 'text.secondary' }} />
      </IconButton>
      {renderShareRoom(false, true)}
      <Drawer
        classes={{
          paper: classes.paper,
        }}
        anchor="right"
        keepMounted
        onClose={toggleMenu}
        open={openMenu}
      >
        <Box height="100%">
          <Box
            display="flex"
            flexDirection="column"
            height="100%"
            flexWrap="nowrap"
          >
            <RoomAppBar
              {...AppBarProps}
              primaryAction={
                <IconButton onClick={toggleMenu} size="large">
                  <Close />
                </IconButton>
              }
            />

            <Box className={classes.list}>
              <List disablePadding>{renderHeaderItems()}</List>
              <Box display="flex" flexDirection="column">
                <Box my={2}>
                  {renderRoomConversation(true, false, onConversationClose)}
                </Box>
                {renderShareRoom(true)}
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default RoomHeadersMenuList;
